import React, { Dispatch, FC, SetStateAction, useEffect } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import {
  RiskAssessmentQuestionnaireHandler
} from 'src/pages/RiskAssessmentQuestionnairePage/RiskAssessmentQuestionnaireHandler/RiskAssessmentQuestionnaireHandler';
import QuestionnaireGeneralInfo from 'src/pages/QuestionnairesPage/QuestionnaireGeneralInfo/QuestionnaireGeneralInfo';
import {
  useGeneralFamilyPedigreeInfo,
  useGetGeneralQuestionnaireInfo,
  completedAssessmentHandler,
  useReturnToLastQuestionMutation
} from 'src/hooks/api/useRiskAssessmentQuestionaire';
import { useActiveTab } from 'src/hooks/useActiveTab';
import { QuestionnaireStatuses, QuestionnairesTypes } from 'src/constans/constans';
import { QuestionnairesPageWrapper } from 'src/pages/QuestionnairesPage/QuestionnairesPage.styles';
import { FamilyTreeSettingsInterface } from 'src/models/RiskAssessmentQuestionnaire';
import { openLinkInNewTab } from 'src/utils/openLinkInNewTab'

interface Props {
  isWidgetActiveTab: boolean
  botTypePreference: string
  familyTreeSettings: FamilyTreeSettingsInterface
  setFamilyTreeSettings: Dispatch<SetStateAction<FamilyTreeSettingsInterface>>
  widgetSource: MessageEventSource
  updatingAudioQueue: (data: string) => void
  stopPlayAndResetAudioQueue: () => void
  stopSpeakingUtterance: () => void
  setIsWidgetFinishedPlay: Dispatch<SetStateAction<boolean>>
  isWidgetFinishedPlay: boolean
  isWidgetStartedPlay: boolean
}

export const QuestionnairesPage: FC<Props> = ({
  isWidgetActiveTab,
  botTypePreference,
  familyTreeSettings,
  setFamilyTreeSettings,
  updatingAudioQueue,
  stopPlayAndResetAudioQueue,
  stopSpeakingUtterance,
  setIsWidgetFinishedPlay,
  isWidgetFinishedPlay,
  isWidgetStartedPlay
}) => {
  const chatId = localStorage.getItem('chatId')
  const { activeTab } = useActiveTab()
  const { riskAssessmentData, reFetchRiskAssessmentData } = useGetGeneralQuestionnaireInfo(chatId);
  const { familyPedigreeData, reFetchFamilyPedigreeData } = useGeneralFamilyPedigreeInfo(chatId);
  const { mutate: returnRequest, isLoading } = useReturnToLastQuestionMutation(() => setCurrentPage(QuestionnairesTypes.FAMILY_PEDIGREE));
  const [currentPage, setCurrentPage] = useLocalStorage('currentQuestionnairePage', QuestionnairesTypes.ALL_QUESTIONNAIRES);
  const currentQuestionnaireStatus = currentPage === QuestionnairesTypes.RISK_ASSESSMENT ? riskAssessmentData?.status : familyPedigreeData?.status

  useEffect(() => {
    reFetchRiskAssessmentData()
    reFetchFamilyPedigreeData()
  }, [currentPage, activeTab, reFetchFamilyPedigreeData, reFetchRiskAssessmentData])

  const onClickHandler = (questionnaireName: QuestionnairesTypes) => {
    if (currentQuestionnaireStatus === QuestionnaireStatuses.COMPLETED && questionnaireName === QuestionnairesTypes.FAMILY_PEDIGREE) {
      return openLinkInNewTab(chatId)
    }
    setIsWidgetFinishedPlay(true)
    stopPlayAndResetAudioQueue()
    stopSpeakingUtterance()

    setCurrentPage(questionnaireName)
  }

  const editAnswersHandler = () => {
    if (isLoading) return
    returnRequest({ chat_id: chatId })
  }

  return (
    <QuestionnairesPageWrapper isWidgetActiveTab={isWidgetActiveTab}>
      {currentPage === QuestionnairesTypes.ALL_QUESTIONNAIRES ? (
        <QuestionnaireGeneralInfo
          title={'Biological Family\'s Cancer History'}
          onClickHandler={() => onClickHandler(QuestionnairesTypes.FAMILY_PEDIGREE)}
          completedAssessmentHandler={() => completedAssessmentHandler(chatId, QuestionnairesTypes.FAMILY_PEDIGREE)}
          answeredBlocks={familyPedigreeData?.questionnaire_progress?.answered_blocks}
          totalBlocks={familyPedigreeData?.questionnaire_progress?.total_blocks}
          questionnaireStatus={familyPedigreeData?.status}
          questionnaireType={QuestionnairesTypes.FAMILY_PEDIGREE}
          editMyAnswersHandler={editAnswersHandler}
          isEditMyAnswersButtonDisable={isLoading}
          hasFamilyTree={true}
          familyTreeSettings={familyTreeSettings}
        />
      ) :
        <RiskAssessmentQuestionnaireHandler
          currentPage={currentPage}
          currentQuestionnaireStatus={currentQuestionnaireStatus}
          botTypePreference={botTypePreference}
          familyTreeSettings={familyTreeSettings}
          setFamilyTreeSettings={setFamilyTreeSettings}
          updatingAudioQueue={updatingAudioQueue}
          stopPlayAndResetAudioQueue={stopPlayAndResetAudioQueue}
          stopSpeakingUtterance={stopSpeakingUtterance}
          isWidgetActiveTab={isWidgetActiveTab}
          isWidgetFinishedPlay={isWidgetFinishedPlay}
          isWidgetStartedPlay={isWidgetStartedPlay}
        />
      }
    </QuestionnairesPageWrapper>
  );
};
